import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Phone, Pause, Play, Settings, Search, Clock, User, Bot, X } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { sessionsAPI, contactsAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { PageSkeleton, SkeletonTabela } from '../components/Skeleton';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pauseMinutes, setPauseMinutes] = useState(30);
  const [configData, setConfigData] = useState({
    permitir_interrupcao: true,
    tempo_reativacao_minutos: 30,
    reativar_apos_inatividade: true,
    mensagem_ia_pausada: '',
    mensagem_ia_reativada: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { darkMode } = useTheme();

  useEffect(() => {
    loadContacts();
    loadConfig();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.list(user.cliente_id);
      setContacts(response.data.messages || []);
    } catch (error) {
      toast.error('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await sessionsAPI.getConfig(user.cliente_id);
      if (response.data) {
        setConfig(response.data);
        setConfigData({
          permitir_interrupcao: response.data.permitir_interrupcao ?? true,
          tempo_reativacao_minutos: response.data.tempo_reativacao_minutos || 30,
          reativar_apos_inatividade: response.data.reativar_apos_inatividade ?? true,
          mensagem_ia_pausada: response.data.mensagem_ia_pausada || '',
          mensagem_ia_reativada: response.data.mensagem_ia_reativada || ''
        });
      }
    } catch (error) {
      console.log('Nenhuma configuração encontrada');
    }
  };

  const handlePauseIA = async () => {
    if (!selectedContact) {
      toast.error('Selecione um contato');
      return;
    }

    try {
      const contact = contacts.find(c => c.id === selectedContact);
      await sessionsAPI.pauseIA({
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        telefone: contact.telefone,
        minutos: pauseMinutes
      });
      
      toast.success(`IA pausada para ${contact.nome} por ${pauseMinutes} minutos`);
      setShowPauseModal(false);
      setSelectedContact('');
      setPauseMinutes(30);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao pausar IA');
    }
  };

  const handleReactivateIA = async (contactId) => {
    try {
      const contact = contacts.find(c => c.id === contactId);
      await sessionsAPI.reactivateIA({
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        telefone: contact.telefone
      });
      
      toast.success(`IA reativada para ${contact.nome}`);
    } catch (error) {
      toast.error('Erro ao reativar IA');
    }
  };

  const handleMarkHuman = async (contactId) => {
    try {
      const contact = contacts.find(c => c.id === contactId);
      await sessionsAPI.markHuman({
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        telefone: contact.telefone
      });
      
      toast.success(`Atendimento humano marcado para ${contact.nome}`);
    } catch (error) {
      toast.error('Erro ao marcar atendimento humano');
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    
    try {
      if (config) {
        await sessionsAPI.updateConfig(user.cliente_id, configData);
        toast.success('Configuração atualizada com sucesso!');
      } else {
        await sessionsAPI.createConfig({
          cliente_id: user.cliente_id,
          ...configData
        });
        toast.success('Configuração criada com sucesso!');
      }
      
      setShowConfigModal(false);
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
    }
  };

  const filteredContacts = Array.isArray(contacts) ? contacts.filter(contact =>
    contact.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.telefone?.includes(searchTerm)
  ) : [];
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Sessões de Atendimento" subtitle="Controle de atendimento humano vs IA">
          <SkeletonTabela colunas={5} linhas={8} />
        </PageSkeleton>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Sessões de Atendimento" subtitle="Controle de atendimento humano vs IA">
            <button
              onClick={() => setShowPauseModal(true)}
              className="gradient-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Pause className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Pausar IA</span>
            </button>
            <button
              onClick={() => setShowConfigModal(true)}
              className="secondary-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Configurações</span>
            </button>
        </PageHeader>

        {/* Configuration Status */}
        {config && (
          <div className={`rounded-lg shadow p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-medium mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Status das Configurações
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center">
                <div className={`p-2 rounded-full ${
                  config.permitir_interrupcao 
                    ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                    : darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'
                }`}>
                  {config.permitir_interrupcao ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Interrupção
                  </p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {config.permitir_interrupcao ? 'Permitida' : 'Bloqueada'}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className={`p-2 rounded-full ${
                  darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                }`}>
                  <Clock className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Reativação
                  </p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {config.tempo_reativacao_minutos} min
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className={`p-2 rounded-full ${
                  config.reativar_apos_inatividade
                    ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                    : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                }`}>
                  <User className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Auto-reativação
                  </p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {config.reativar_apos_inatividade ? 'Ativa' : 'Inativa'}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className={`p-2 rounded-full ${
                  darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                }`}>
                  <Bot className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Sistema
                  </p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Configurado
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={`h-5 w-5 ${
              darkMode ? 'text-gray-400' : 'text-gray-400'
            }`} />
          </div>
          <input
            type="text"
            placeholder="Buscar contatos..."
            className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-nomoz-orange focus:border-nomoz-orange ${
              darkMode
                ? 'bg-zinc-800 border-gray-700 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Contacts List */}
        <div className={`shadow overflow-hidden sm:rounded-md ${
          darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <ul className={`divide-y ${
            darkMode ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            {filteredContacts.length === 0 ? (
              <li className="px-6 py-4 text-center">
                <Phone className={`mx-auto h-12 w-12 ${
                  darkMode ? 'text-gray-600' : 'text-gray-400'
                } mb-4`} />
                <h3 className={`text-sm font-medium ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Nenhum contato
                </h3>
                <p className={`text-sm ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Adicione contatos para gerenciar sessões.
                </p>
              </li>
            ) : (
              filteredContacts.map((contact) => (
                <li key={contact.id} className={`px-6 py-4 ${
                  darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          darkMode ? 'bg-green-900/20' : 'bg-green-100'
                        }`}>
                          <Phone className={`h-6 w-6 ${
                            darkMode ? 'text-green-400' : 'text-green-600'
                          }`} />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {contact.nome}
                        </div>
                        <div className={`text-sm ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {contact.telefone}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        darkMode
                          ? 'bg-blue-900/20 text-blue-400'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        <Bot className="h-3 w-3 mr-1" />
                        IA Ativa
                      </span>
                      <button
                        onClick={() => handleReactivateIA(contact.id)}
                        className={darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-900'}
                        title="Reativar IA"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMarkHuman(contact.id)}
                        className={darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-900'}
                        title="Marcar atendimento humano"
                      >
                        <User className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Pause IA Modal */}
        {showPauseModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 transition-opacity">
            <div className={`relative top-20 p-6 w-full max-w-sm mx-4 sm:mx-auto shadow-lg rounded-lg ${
              darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Pausar IA para Contato
                </h3>
                <button
                  onClick={() => {
                    setShowPauseModal(false);
                    setSelectedContact('');
                    setPauseMinutes(30);
                  }}
                  className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    Selecionar Contato
                  </label>
                  <select
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    value={selectedContact}
                    onChange={(e) => setSelectedContact(e.target.value)}
                  >
                    <option value="">Selecione um contato...</option>
                    {Array.isArray(contacts) && contacts.map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.nome} - {contact.telefone}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    Pausar por (minutos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    value={pauseMinutes}
                    onChange={(e) => setPauseMinutes(parseInt(e.target.value))}
                  />
                  <p className={`text-xs mt-1 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Deixe vazio para pausar indefinidamente
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowPauseModal(false);
                      setSelectedContact('');
                      setPauseMinutes(30);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange ${
                      darkMode
                        ? 'border border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600'
                        : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handlePauseIA}
                    className="gradient-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange"
                  >
                    Pausar IA
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Config Modal */}
        {showConfigModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 transition-opacity">
            <div className={`relative top-10 mx-auto p-6 w-full max-w-2xl shadow-lg rounded-lg ${
              darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Configurações de Sessão
                </h3>
                <button
                  onClick={() => setShowConfigModal(false)}
                  className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleSaveConfig} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className={`h-4 w-4 focus:ring-nomoz-orange ${
                          darkMode
                            ? 'text-green-600 border-gray-600 bg-zinc-600'
                            : 'text-green-600 border-gray-300'
                        }`}
                        checked={configData.permitir_interrupcao}
                        onChange={(e) => setConfigData({...configData, permitir_interrupcao: e.target.checked})}
                      />
                      <label className={`ml-2 block text-sm ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Permitir interrupção da IA
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className={`h-4 w-4 focus:ring-blue-500 ${
                          darkMode
                            ? 'text-blue-600 border-gray-600 bg-zinc-600'
                            : 'text-blue-600 border-gray-300'
                        }`}
                        checked={configData.reativar_apos_inatividade}
                        onChange={(e) => setConfigData({...configData, reativar_apos_inatividade: e.target.checked})}
                      />
                      <label className={`ml-2 block text-sm ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Reativar após inatividade humana
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${
                      darkMode ? 'text-white' : 'text-gray-700'
                    }`}>
                      Tempo para reativação (minutos)
                    </label>
                    <input
                      type="number"
                      min="1"
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={configData.tempo_reativacao_minutos}
                      onChange={(e) => setConfigData({...configData, tempo_reativacao_minutos: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    Mensagem quando IA é pausada
                  </label>
                  <textarea
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    rows={3}
                    value={configData.mensagem_ia_pausada}
                    onChange={(e) => setConfigData({...configData, mensagem_ia_pausada: e.target.value})}
                    placeholder="Transferindo para atendimento humano. Em breve você será atendido!"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    Mensagem quando IA é reativada
                  </label>
                  <textarea
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    rows={3}
                    value={configData.mensagem_ia_reativada}
                    onChange={(e) => setConfigData({...configData, mensagem_ia_reativada: e.target.value})}
                    placeholder="Olá! Sou o assistente virtual e estou aqui para ajudá-lo novamente."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowConfigModal(false)}
                    className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange ${
                      darkMode
                        ? 'border border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600'
                        : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="secondary-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange"
                  >
                    Salvar Configuração
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Sessions;